import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import P11Layout from "./P11Layout";
import P11StampCard from "./P11StampCard";
import { P11, fmtDate } from "./p11shared";
import { useP11Lang } from "./p11lang";

// Dynamisch deelnemersdashboard. Toont nooit tellingen of bezetting van anderen.
export default function P11Dashboard() {
  const nav = useNavigate();
  const location = useLocation();
  const { lang, tt } = useP11Lang();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/project11/me/dashboard")
      .then(({ data }) => {
        setData(data);
        // eenmalige voorbereiding bij eerste bezoek (expliciet via welcome-flow óf nog niet gezien)
        if (!data.participant.preparation_seen && (location.state?.firstVisit || location.state === null)) {
          // alleen automatisch bij eerste bezoek ná identiteit; anders via EQUIPMENT link
          if (location.state?.firstVisit) nav("/project11/preparation", { replace: true });
        }
      })
      .catch(() => nav("/project11/login"));
  }, [nav]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="p11-root min-h-screen flex items-center justify-center"><span className="p11-label">…</span></div>;

  const { state } = data;
  const headline = {
    BEFORE_FIRST: tt("journey_starts"), ACTIVE: tt("keep_showing"), MISSED_SESSION: tt("journey_continues"),
    COMPLETED: tt("completed_title"), INCOMPLETE: tt("incomplete_title"), WAITLIST: tt("waitlist_title"), CANCELLED: tt("incomplete_title"),
    REVIEW: tt("under_review_title"), REJECTED: tt("rejected_title"),
  }[state] || tt("keep_showing");

  return (
    <P11Layout>
      <div data-testid="p11-dashboard">
        <div className="p11-label">JOH. BOXING / PROJECT 11*</div>
        <h1 className="p11-h1 text-3xl md:text-4xl mt-4" data-testid="p11-dash-name">{tt("welcome_back")} {(data.participant.first_name || "").toUpperCase()}.</h1>
        <div className="flex items-center gap-4 mt-4">
          {data.number_display && <span className="p11-mono text-lg" style={{ color: P11.yellow }} data-testid="p11-dash-number">{tt("participant")} {data.number_display}</span>}
          <span className="p11-label" data-testid="p11-dash-status">STATUS / {state === "WAITLIST" ? "WAITLIST" : state === "CANCELLED" ? "CANCELLED" : state === "REVIEW" ? (lang === "nl" ? "IN AFWACHTING VAN GOEDKEURING" : "AWAITING APPROVAL") : state === "REJECTED" ? "REJECTED" : "ACTIVE"}</span>
        </div>

        <hr className="p11-divider my-10" />

        <h2 className="p11-h2 text-2xl md:text-3xl" data-testid="p11-dash-headline">{headline}</h2>
        {state === "MISSED_SESSION" && <p className="text-sm mt-4 leading-relaxed" style={{ color: P11.gray2 }} data-testid="p11-dash-missed-note">{tt("missed_expl")}</p>}
        {state === "COMPLETED" && <p className="text-base mt-4 leading-relaxed" style={{ color: P11.gray2 }}>{tt("completed_body")}</p>}
        {state === "INCOMPLETE" && <p className="text-sm mt-4" style={{ color: P11.gray2 }}>{tt("incomplete_body")}</p>}
        {state === "WAITLIST" && <p className="text-sm mt-4" style={{ color: P11.gray2 }} data-testid="p11-dash-waitlist">{tt("waitlist_body")}</p>}
        {state === "REVIEW" && (
          <div className="mt-6 border px-4 py-3 inline-flex items-center gap-3" style={{ borderColor: P11.yellow }} data-testid="p11-dash-review">
            <span className="w-1.5 h-1.5 p11-blink" style={{ background: P11.yellow }} />
            <span className="p11-label-yellow">{tt("under_review_status")}</span>
          </div>
        )}
        {state === "REVIEW" && <p className="text-sm mt-4 max-w-md" style={{ color: P11.gray2 }}>{tt("under_review_body")}</p>}
        {state === "REJECTED" && <p className="text-sm mt-4 max-w-md" style={{ color: P11.gray2 }} data-testid="p11-dash-rejected">{tt("rejected_body")}</p>}

        {state !== "REVIEW" && state !== "REJECTED" && (
        <div className="mt-10">
          <div className="flex items-end justify-between mb-4">
            <span className="p11-mono text-2xl" data-testid="p11-dash-count">{String(data.verified).padStart(2, "0")} / 08</span>
            <span className="p11-label">{tt("verified_l")}</span>
          </div>
          <P11StampCard stamps={data.stamps} />
        </div>
        )}

        {data.next_session && !["COMPLETED", "INCOMPLETE", "WAITLIST", "REVIEW", "REJECTED", "CANCELLED"].includes(state) && (
          <div className="mt-10 border p-6" style={{ borderColor: P11.border }} data-testid="p11-dash-next">
            <div className="p11-label-yellow">{tt("next_training")}</div>
            <div className="p11-h2 text-xl mt-3">SESSIE {String(data.next_session.number).padStart(2, "0")}</div>
            <div className="text-sm mt-2" style={{ color: P11.gray2 }}>
              {fmtDate(data.next_session.date, lang)} · {data.next_session.start_time}–{data.next_session.end_time}
            </div>
            <div className="p11-label mt-2">{data.next_session.location}</div>
          </div>
        )}

        <div className="mt-10 grid grid-cols-2 gap-3">
          <Link to="/project11/sessions" className="p11-btn-ghost justify-center" data-testid="p11-dash-sessions">{tt("my_sessions")}</Link>
          <Link to="/project11/record" className="p11-btn-ghost justify-center" data-testid="p11-dash-record">{tt("the_record")}</Link>
          <Link to="/project11/profile" className="p11-btn-ghost justify-center" data-testid="p11-dash-profile">{tt("my_profile")}</Link>
          <Link to="/project11/preparation" className="p11-btn-ghost justify-center" data-testid="p11-dash-equipment">{tt("equipment")}</Link>
        </div>
      </div>
    </P11Layout>
  );
}
